import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { DateEx } from '../../../../functions/date-ex';
import { SelectComponent } from '../../../core/controls/select.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { AbsenceDetailComponent } from './absence-detail.component';
import { LeaveCalendarLegendComponent } from './leave-calendar-legend.component';
import {
  calculateMonthDifference,
  hasDateChanged as helperHasDateChanged,
  updateMonthViewSelections as helperUpdateMonthViewSelections,
  shouldNavigateToDate,
} from './leave-calendar-navigation.helper';
import {
  CalendarState,
  NavigationContext,
  VALID_TRANSITIONS,
} from './leave-calendar-state';
import {
  applyAbsencesToDays,
  isSameMonthAndYear,
} from './leave-calendar-utils';
import { Day, MonthViewComponent } from './month-view.component';

@Component({
  selector: 'app-leave-calendar',
  imports: [
    MonthViewComponent,
    ActionRowComponent,
    CommonModule,
    LeaveCalendarLegendComponent,
    AbsenceDetailComponent,
    SelectComponent,
  ],
  template: `
    <div class="tw-flex tw-pt-2">
      <div>
        <app-action-row>
          <div left1>
            <app-leave-calendar-legend class="tw-py-8" />
          </div>
          <div right1>
            <div class="tw-grow tw-min-w-64">
              @if (level.enabled) {
                <app-select
                  [options]="levels"
                  optionLabel="description"
                  optionValue="value"
                  class="tw-basis-1/5"
                  [virtualScroll]="false"
                  [control]="level"
                  label="Level"
                  [multiple]="false"
                  [filter]="false"
                  [showClear]="false"
                  [showHelpText]="false"
                />
              }
              <!-- Claim dropdown is currently not visible. It is not being used at the moment 
                   but could be implemented down the line if needed for filtering by specific claims. -->
              <!-- @if (level.value === 'claim') {
                <app-select
                  [options]="claims"
                  optionLabel="description"
                  optionValue="value"
                  class="tw-basis-1/5 tw-pt-4"
                  [virtualScroll]="false"
                  [control]="claim"
                  label="Claim"
                  [multiple]="false"
                  [filter]="false"
                  [showClear]="false"
                  [showHelpText]="false"
                />
              } -->
            </div>
          </div>
        </app-action-row>
        <div class="tw-grow tw-pt-2">
          <div class="tw-flex tw-gap-6 tw-pt-4">
            @for (index of monthIndices.slice(0, 4); track index) {
              <app-month-view
                [month]="getMonthForIndex(index)"
                [showNavigation]="index === 0"
                [navigationMonths]="calendar === 'yearly' ? 12 : 1"
                [dateMenu]="index === 0 ? dateMenu : undefined"
                (monthChanged)="
                  index === 0 ? setMonth($event) : monthChanged($event)
                "
                (dateChanged)="dateChanged($event)"
              />
            }
          </div>

          <div
            [ngClass]="{
              'tw-hidden': calendar === 'monthly',
              'tw-block': calendar === 'yearly',
            }"
          >
            <div class="tw-flex tw-gap-6 tw-pt-4">
              @for (index of monthIndices.slice(4, 8); track index) {
                <app-month-view
                  [month]="getMonthForIndex(index)"
                  [showNavigation]="false"
                  (monthChanged)="monthChanged($event)"
                  (dateChanged)="dateChanged($event)"
                />
              }
            </div>

            <div class="tw-flex tw-gap-6 tw-pt-4">
              @for (index of monthIndices.slice(8, 12); track index) {
                <app-month-view
                  [month]="getMonthForIndex(index)"
                  [showNavigation]="false"
                  (monthChanged)="monthChanged($event)"
                  (dateChanged)="dateChanged($event)"
                />
              }
            </div>
          </div>
        </div>
      </div>

      <div
        class="tw-border-l tw-ml-6 tw-pl-6 tw-pb-4 tw-min-w-[250px] tw-flex tw-justify-center tw-flex-col"
      >
        <app-absence-detail [day]="selectedDay" />
      </div>
    </div>
  `,
  styles: ``,
})
export class LeaveCalendarComponent
  implements OnChanges, AfterViewInit, OnInit
{
  @ViewChildren(MonthViewComponent)
  monthViews!: QueryList<MonthViewComponent>;

  monthIndices = Array.from({ length: 12 }, (_, i) => i);

  @Input()
  startDate: Date = new Date();

  @Input()
  calendar = 'monthly';

  @Input()
  absencesByDate: Map<string, any[]>;

  @Input()
  dateMenu: Day[] = [];

  @Input()
  level: FormControl<string>;

  @Input()
  levels: { description: string; value: string }[] = [];

  @Input()
  claims: { description: string; value: string }[] = [];

  @Input()
  claim: FormControl<string>;

  @Input()
  loading = false;

  selectedDay: Day;

  // Replace multiple flags with state machine
  private navContext = new NavigationContext();

  // Operation queue to track pending operations during state transitions
  private pendingOperations = new Set<'refresh'>();

  // Navigation subject for robust async handling
  private navigationSubject = new Subject<number>();

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    // Set up navigation handling with debouncing for robust async behavior
    this.navigationSubject
      .pipe(
        tap(() => this.transitionTo('NAVIGATING')),
        debounceTime(50), // Debounce rapid navigation requests
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((monthDifference) => {
        const firstMonthView = this.monthViews?.first;
        if (firstMonthView) {
          firstMonthView.monthClick(monthDifference);
        }
        this.transitionTo('READY');
      });
  }

  // State transition with validation
  private transitionTo(newState: CalendarState): void {
    const allowed = VALID_TRANSITIONS[this.navContext.state];
    if (!allowed?.includes(newState)) {
      return;
    }
    this.navContext.state = newState;
  }

  // Guard methods
  private canNavigate(): boolean {
    return this.navContext.state === 'READY';
  }

  private canRefresh(): boolean {
    return (
      this.navContext.state === 'READY' ||
      this.navContext.state === 'INITIALIZING'
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Refresh calendar when absences data changes
    if (changes['absencesByDate']) {
      if (this.navContext.state === 'READY') {
        // View is ready, refresh immediately
        this.refreshCalendar();
      } else {
        // View not ready yet, queue for refresh after initialization
        this.pendingOperations.add('refresh');
      }
    }
  }

  ngAfterViewInit(): void {
    // Transition to READY state
    this.transitionTo('READY');

    // Initialize the first month view
    const firstMonthView = this.monthViews.first;
    if (firstMonthView) {
      firstMonthView.month = new Date();
    }

    // Process any pending operations that accumulated during initialization
    this.processPendingOperations();

    // Select today's date after view is fully initialized
    setTimeout(() => {
      this.selectTodaysDate();
    }, 0);
  }

  private selectTodaysDate(): void {
    const today = new Date();
    const todayDay = new Day(today, today.getDate().toString());
    this.dateChanged(todayDay);
  }

  private processPendingOperations(): void {
    if (this.navContext.state !== 'READY') {
      return;
    }

    // Process refresh operations
    if (this.pendingOperations.has('refresh')) {
      this.pendingOperations.delete('refresh');
      this.refreshCalendar();
    }
  }

  private refreshCalendar(): void {
    // Refresh calendar view after data changes
    if (!this.canRefresh()) {
      return;
    }

    this.transitionTo('REFRESHING');

    // Recalculate days for all month views
    // Each view will call monthChanged which applies absences to its days
    const views = this.getViews();
    for (const view of views) {
      if (view) {
        view.calculateDays();
      }
    }

    this.transitionTo('READY');

    // Process any operations that queued during refresh
    if (this.pendingOperations.size > 0) {
      this.processPendingOperations();
    }
  }

  getViews(): MonthViewComponent[] {
    return this.monthViews ? this.monthViews.toArray() : [];
  }

  getMonthForIndex(index: number): Date {
    return DateEx.addMonths(this.startDate, index);
  }

  setMonth(days: Day[]) {
    const views = this.getViews();
    if (views.length === 0) {
      return;
    }

    // Get base month from first valid view, or use current startDate as fallback
    const firstView = views.find((view) => view && view.month);
    const baseMonth: Date = firstView ? firstView.month : this.startDate;

    // Update startDate so template bindings use the new base month
    this.startDate = new Date(baseMonth);

    // Batch updates: collect all month updates first to minimize change detection cycles
    const updates: { view: MonthViewComponent; month: Date }[] = [];
    const selectedDate = this.selectedDay?.date;

    for (let i = 0; i < views.length; i++) {
      const monthView = views[i];
      if (!monthView) continue;

      // Calculate target month
      const targetMonth = DateEx.addMonths(this.startDate, i);

      // Queue update only if different to avoid unnecessary recalculations
      if (
        !monthView.month ||
        monthView.month.getTime() !== targetMonth.getTime()
      ) {
        updates.push({ view: monthView, month: targetMonth });
      }

      // Update selected date on all views if a day is selected
      if (selectedDate) {
        monthView.selectedDate = selectedDate;
      }
    }

    // Apply all month updates together (Angular batches change detection)
    for (const update of updates) {
      update.view.month = update.month;
    }

    this.monthChanged(days);
  }

  dateChanged(day: Day) {
    if (!this.canNavigate()) {
      return; // Ignore navigation requests during invalid states
    }

    this.selectedDay = day;
    const currentDate = this.selectedDay?.date;

    // Check if the selected date actually changed
    if (
      helperHasDateChanged(
        this.navContext.previousSelectedDate,
        currentDate,
        DateEx.dateEquals,
      )
    ) {
      // If the selected month is not visible, navigate to it
      if (currentDate && shouldNavigateToDate(currentDate, this.getViews())) {
        this.navigateToDate(currentDate);
      } else if (currentDate) {
        // Update previous selected date
        this.navContext.previousSelectedDate = new Date(currentDate);
      }
    }

    // Update selections on all month views and get the day object with absence data
    const dayWithData = helperUpdateMonthViewSelections(
      this.selectedDay?.date,
      this.getViews(),
      isSameMonthAndYear,
      DateEx.dateEquals,
    );

    // Update selectedDay with the actual day object from the month view that has absence data
    if (dayWithData) {
      this.selectedDay = dayWithData;
    }
  }

  private navigateToDate(targetDate: Date): void {
    const firstMonthView = this.monthViews?.first;
    if (!firstMonthView || !firstMonthView.month) {
      return;
    }

    const monthDifference = calculateMonthDifference(
      firstMonthView.month,
      targetDate,
      this.calendar,
    );

    // Emit navigation request through Subject for robust async handling
    this.navigationSubject.next(monthDifference);

    // Update previous selected date
    this.navContext.previousSelectedDate = new Date(targetDate);
  }

  monthChanged(days: Day[]) {
    // Apply absence data to calendar days
    // Note: Each month view has its own days array, so we need to apply to each
    applyAbsencesToDays(days, this.absencesByDate);

    // Only update selection if a day is currently selected
    if (this.selectedDay) {
      this.dateChanged(this.selectedDay);
    }
  }
}
